import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, FileImage, FileAudio, FileText, X, Loader2 } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,audio/*,application/pdf";
const MAX = 15 * 1024 * 1024;

const iconFor = (t) => (t.startsWith("image/") ? FileImage : t.startsWith("audio/") ? FileAudio : FileText);
const fmt = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`);

export default function EvidenceUpload({ slug, files, onChange, max = 5 }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    if (files.length + list.length > max) return toast.error(`Máximo de ${max} anexos`);
    setUploading(true);
    for (const f of list) {
      if (f.size > MAX) { toast.error(`${f.name}: excede 15 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/public/${slug}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`${f.name}: ${apiError(err)}`);
      }
    }
    setUploading(false);
  };

  return (
    <div data-testid="evidence-upload">
      <input ref={ref} type="file" accept={ACCEPT} multiple className="hidden" onChange={pick} data-testid="evidence-file-input" />
      <Button type="button" variant="outline" disabled={uploading || files.length >= max} onClick={() => ref.current?.click()} data-testid="evidence-add-btn" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
        {uploading ? "Enviando…" : "Anexar evidência"}
      </Button>
      <p className="text-xs text-slate-500 mt-2">Imagens, áudios ou PDF · até 15 MB cada · máx. {max} arquivos. Nenhum metadado do dispositivo é armazenado.</p>
      {files.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="evidence-list">
          {files.map((f) => {
            const Icon = iconFor(f.content_type || "");
            return (
              <li key={f.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/60 border border-slate-700 text-sm" data-testid="evidence-item">
                <Icon className="w-4 h-4 text-teal-400 shrink-0" />
                <span className="text-slate-200 truncate flex-1">{f.filename}</span>
                <span className="text-xs text-slate-500">{fmt(f.size)}</span>
                <button type="button" onClick={() => onChange((prev) => prev.filter((x) => x.id !== f.id))} className="text-slate-500 hover:text-rose-400" data-testid="evidence-remove-btn"><X className="w-4 h-4" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
